import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  shorthands,
  Text,
  Spinner,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  Badge,
} from "@fluentui/react-components";
import {
  MailRegular,
  ArrowDownRegular,
  DismissCircleRegular,
  CalendarRegular,
} from "@fluentui/react-icons";
import { Letter } from "../types/letter";
import { letterService } from "../services/letterService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"),
    ...shorthands.padding("16px", "0"),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.padding("0", "16px"),
  },
  title: {
    fontSize: "14px",
    fontWeight: "700",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#0B5D5D",
  },
  jumpButton: {
    minWidth: "auto",
    ...shorthands.padding("4px", "12px"),
  },
  timelineContainer: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("0"),
    maxHeight: "300px",
    overflowY: "auto",
    ...shorthands.padding("0", "16px"),
  },
  timelineItem: {
    display: "flex",
    ...shorthands.gap("12px"),
    position: "relative",
    ...shorthands.padding("12px", "0"),
  },
  timelineConnector: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "24px",
    flexShrink: 0,
  },
  timelineDot: {
    width: "10px",
    height: "10px",
    ...shorthands.borderRadius("50%"),
    backgroundColor: "#0B5D5D",
    flexShrink: 0,
    marginTop: "6px",
  },
  timelineLine: {
    width: "2px",
    flexGrow: 1,
    backgroundColor: "#E0E0E0",
    marginTop: "4px",
  },
  letterContent: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
    flexGrow: 1,
    minWidth: 0,
  },
  letterTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#1A1A1A",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  letterDate: {
    fontSize: "11px",
    color: "#666",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  directionBadge: {
    fontSize: "10px",
    height: "18px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("40px", "16px"),
    ...shorthands.gap("12px"),
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.padding("40px", "20px"),
    ...shorthands.gap("8px"),
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "32px",
    color: "#999",
  },
  loadMoreContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    ...shorthands.padding("12px", "16px"),
  },
  loadMoreButton: {
    minWidth: "120px",
    backgroundColor: "#0B5D5D",
    color: "#ffffff",
    ":hover": {
      backgroundColor: "#084A4A",
    },
    ":active": {
      backgroundColor: "#063838",
    },
  },
});

interface LetterTimelineProps {
  companyName: string;
  baseCollectionName: string;
  onScrollToBottom?: () => void;
}

export const LetterTimeline: React.FC<LetterTimelineProps> = ({
  companyName,
  baseCollectionName,
  onScrollToBottom,
}) => {
  const LETTERS_PAGE_SIZE = 3;
  const styles = useStyles();
  const [letters, setLetters] = useState<Letter[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Fetch initial letters when component mounts
  const fetchLetters = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log(`[LETTER_TIMELINE] Fetching initial letters for tender: ${baseCollectionName}`);
      const result = await letterService.getLettersForTender(
        companyName,
        baseCollectionName,
        LETTERS_PAGE_SIZE // Initial page size
      );
      setLetters(result.data.items);
      setTotalCount(result.data.totalCount || result.data.items.length);
      setCursor(result.data.cursor);
      setHasMore(result.data.hasMore);
      console.log(`[LETTER_TIMELINE] ✅ Loaded ${result.data.items.length} letters (totalCount: ${result.data.totalCount}, hasMore: ${result.data.hasMore})`);
    } catch (err) {
      console.error(`[LETTER_TIMELINE] Error fetching letters:`, err);
      setError(err instanceof Error ? err.message : "Failed to load letters");
    } finally {
      setIsLoading(false);
    }
  }, [companyName, baseCollectionName]);

  // Load more letters
  const loadMoreLetters = useCallback(async () => {
    if (!cursor || !hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    setError(null);

    try {
      console.log(`[LETTER_TIMELINE] Loading more letters for tender: ${baseCollectionName}`);
      const result = await letterService.getLettersForTender(
        companyName,
        baseCollectionName,
        LETTERS_PAGE_SIZE, // Load more page size
        cursor
      );
      setLetters(prev => [...prev, ...result.data.items]);
      setCursor(result.data.cursor);
      setHasMore(result.data.hasMore);
      console.log(`[LETTER_TIMELINE] ✅ Loaded ${result.data.items.length} more letters (totalLoaded: ${letters.length + result.data.items.length}/${totalCount}, hasMore: ${result.data.hasMore})`);
    } catch (err) {
      console.error(`[LETTER_TIMELINE] Error loading more letters:`, err);
      setError(err instanceof Error ? err.message : "Failed to load more letters");
    } finally {
      setIsLoadingMore(false);
    }
  }, [companyName, baseCollectionName, cursor, hasMore, isLoadingMore, letters.length, totalCount]);

  useEffect(() => {
    fetchLetters();
  }, [fetchLetters]);

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getDirectionColor = (direction: string): "success" | "informative" => {
    return direction === 'inbound' ? 'informative' : 'success';
  };

  const getDirectionLabel = (direction: string): string => {
    return direction === 'inbound' ? 'Received' : 'Sent';
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" />
        <Text style={{ fontSize: "12px", color: "#666" }}>Loading letters...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{error}</MessageBarBody>
      </MessageBar>
    );
  }

  if (letters.length === 0) {
    return (
      <div className={styles.emptyState}>
        <MailRegular className={styles.emptyIcon} />
        <Text style={{ fontSize: "12px", fontWeight: "600" }}>No letters from this thread yet</Text>
        <Text style={{ fontSize: "11px", color: "#666" }}>
          This is the first letter in this thread, for this tender
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Text className={styles.title}>
          All letters ({totalCount})
        </Text>
        {onScrollToBottom && (
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowDownRegular />}
            onClick={onScrollToBottom}
            className={styles.jumpButton}
          >
            Jump to Form
          </Button>
        )}
      </div>

      <div className={styles.timelineContainer}>
        {letters.map((letter, index) => (
          <div key={letter.sk} className={styles.timelineItem}>
            <div className={styles.timelineConnector}>
              <div className={styles.timelineDot} />
              {index < letters.length - 1 && <div className={styles.timelineLine} />}
            </div>
            <div className={styles.letterContent}>
              <div className={styles.letterTitle} title={letter.title}>
                {letter.title}
              </div>
              <div className={styles.letterDate}>
                <CalendarRegular style={{ fontSize: "11px" }} />
                {formatDate(letter.user_letter_date)}
                <Badge
                  appearance="outline"
                  color={getDirectionColor(letter.direction)}
                  className={styles.directionBadge}
                >
                  {getDirectionLabel(letter.direction)}
                </Badge>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className={styles.loadMoreContainer}>
          <Button
            appearance="primary"
            className={styles.loadMoreButton}
            onClick={loadMoreLetters}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LetterTimeline;
